import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Dumbbell, Target, Calendar, AlertTriangle, Zap, RotateCw, Crown, Camera, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { postWithRetry } from "@/lib/retryablePost";
import { useAuth } from "@/context/AuthContext";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";
import { LockedFeatureCard } from "@/components/LockedFeatureCard";
import ExerciseLine from "@/components/ExerciseLine";

// Fitness Coach — generates a real personalised plan from goal + focus + equipment.
// Free users: 1 plan/day. Premium: unlimited.

const GOALS = [
  { value: "lose-weight",         label: "Lose weight" },
  { value: "gain-muscle",         label: "Gain muscle" },
  { value: "stay-healthy",        label: "Stay healthy" },
  { value: "build-strength",      label: "Build strength" },
  { value: "improve-athletic",    label: "Improve athletic performance" },
];

const FEMALE_FOCUS = ["glutes", "legs", "core", "full-body", "weight-loss"];
const MALE_FOCUS   = ["chest", "back", "arms", "legs", "core", "full-body"];

const EXPERIENCE = ["beginner", "intermediate", "advanced"];
const EQUIPMENT  = ["bodyweight", "home", "bands", "gym"];

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" className={`chip ${active ? "active" : ""}`} onClick={onClick} data-testid={testId}>
      {children}
    </button>
  );
}

export default function FitnessCoachScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = !!user?.premium;
  const [profile, setProfile] = useState({});
  const [goal, setGoal] = useState("");
  const [focus, setFocus] = useState([]);
  const [experience, setExperience] = useState("beginner");
  const [days, setDays] = useState(3);
  const [equipment, setEquipment] = useState("home");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [lastInput, setLastInput] = useState(null);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data || {})).catch(() => {});
    // Spec: a returning user must NOT see an old generated plan automatically.
    // They re-run the form each time they open the screen.
  }, []);

  const gender = (profile.gender || "").toLowerCase();
  const focusOptions = gender === "female" ? FEMALE_FOCUS : MALE_FOCUS;

  const toggleFocus = (v) =>
    setFocus((f) => (f.includes(v) ? f.filter((x) => x !== v) : [...f, v]));

  const generate = async () => {
    if (!goal) return;
    const payload = { goal, focus_areas: focus, experience, days_per_week: days, equipment, notes };
    setLastInput(payload);
    setError(null);
    setLoading(true);
    setPlan(null);
    try {
      const data = await postWithRetry("/fitness/plan", payload, { timeout: 60_000, scope: "fitness" });
      setPlan(data);
      try { track("feature_completed", { feature: "fitness", sub_action: "plan_generated" }); } catch { /* ignore */ }
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally {
      setLoading(false);
    }
  };

  const retry = () => lastInput && generate();
  const changePreferences = () => { setError(null); setPlan(null); };

  return (
    <div className="screen with-nav fade-in" data-testid="fitness-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/improve")} aria-label="Back" data-testid="fitness-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">FITNESS</div>
        {isPremium ? (
          <span className="premium-pill" data-testid="fitness-quota-pill" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Crown size={10} strokeWidth={2} /> PREMIUM
          </span>
        ) : (
          <span className="premium-pill" data-testid="fitness-quota-pill">FREE · 1 EVERY 2 DAYS</span>
        )}
      </div>

      {!plan && !loading && (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>FITNESS COACH</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.12 }}>
            Build your body.<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Photo-first.</em>
          </h2>

          {/* PRIMARY PATH — Body Goal Coach (photo comparison) */}
          <button
            type="button"
            onClick={() => navigate("/improve/body-goal")}
            data-testid="fitness-body-goal-cta"
            style={{
              width: "100%",
              padding: "18px 18px 20px",
              marginBottom: 22,
              textAlign: "left",
              cursor: "pointer",
              background: "linear-gradient(135deg, rgba(232,211,185,0.18) 0%, rgba(232,211,185,0.05) 100%)",
              border: "1px solid var(--gold-line)",
              borderRadius: 18,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div className="eyebrow" style={{ color: "var(--gold)", fontSize: 9 }}>
              PHOTO-BASED · MOST POPULAR
            </div>
            <div className="h2" style={{ color: "var(--text)", fontSize: 20, lineHeight: 1.2, fontFamily: "'Fraunces', serif", fontWeight: 400 }}>
              Upload current + dream body.<br/>
              <em style={{ fontStyle: "italic", fontWeight: 300, color: "var(--gold)" }}>Get a realistic plan.</em>
            </div>

            {/* Visual hint — two photo placeholders */}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ flex: 1, aspectRatio: "3 / 4", borderRadius: 8, background: "var(--surface-soft)", border: "1px dashed var(--line)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-faint)", maxHeight: 110 }}>
                <div style={{ textAlign: "center" }}>
                  <Camera size={18} strokeWidth={1.4}/>
                  <div className="tiny" style={{ marginTop: 4, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase" }}>You now</div>
                </div>
              </div>
              <Target size={18} strokeWidth={1.5} color="var(--gold)" style={{ flexShrink: 0 }}/>
              <div style={{ flex: 1, aspectRatio: "3 / 4", borderRadius: 8, background: "rgba(232,211,185,0.12)", border: "1px dashed var(--gold-line)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gold)", maxHeight: 110 }}>
                <div style={{ textAlign: "center" }}>
                  <Camera size={18} strokeWidth={1.4}/>
                  <div className="tiny" style={{ marginTop: 4, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--gold)" }}>Goal body</div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--gold)", fontSize: 12, fontWeight: 600, letterSpacing: "0.06em" }}>
              <Sparkles size={13} strokeWidth={1.8} />
              Start photo comparison →
            </div>
          </button>

          {/* Secondary path — quick form below */}
          <div className="eyebrow" style={{ marginBottom: 10, color: "var(--text-faint)" }}>OR — QUICK PICKER (NO PHOTOS)</div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>WHAT&apos;S YOUR GOAL?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {GOALS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  className={`tile ${goal === g.value ? "active" : ""}`}
                  onClick={() => setGoal(g.value)}
                  data-testid={`fitness-goal-${g.value}`}
                  style={{
                    flexDirection: "row", alignItems: "center", textAlign: "left",
                    borderColor: goal === g.value ? "var(--gold-line)" : "var(--line)",
                    background: goal === g.value ? "var(--gold-soft)" : "var(--bg-elev)",
                  }}
                >
                  <div className="tile-icon" style={{ background: goal === g.value ? "var(--gold-soft)" : "var(--surface-soft)" }}>
                    <Target size={16} color={goal === g.value ? "var(--gold)" : undefined} strokeWidth={1.7} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>{g.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>FOCUS AREAS ({gender === "female" ? "female menu" : "general menu"})</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {focusOptions.map((f) => (
                <Chip key={f} active={focus.includes(f)} onClick={() => toggleFocus(f)} testId={`fitness-focus-${f}`}>
                  {f.replace(/-/g, " ")}
                </Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>EXPERIENCE</div>
            <div style={{ display: "flex", gap: 6 }}>
              {EXPERIENCE.map((e) => (
                <Chip key={e} active={experience === e} onClick={() => setExperience(e)} testId={`fitness-exp-${e}`}>
                  {e}
                </Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>DAYS / WEEK</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[2, 3, 4, 5, 6].map((d) => (
                <Chip key={d} active={days === d} onClick={() => setDays(d)} testId={`fitness-days-${d}`}>
                  {d}
                </Chip>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>EQUIPMENT</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {EQUIPMENT.map((e) => (
                <Chip key={e} active={equipment === e} onClick={() => setEquipment(e)} testId={`fitness-equip-${e}`}>
                  {e}
                </Chip>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-gold"
            onClick={generate}
            disabled={!goal}
            data-testid="fitness-generate-btn"
            style={{ marginTop: 14, width: "100%", justifyContent: "center" }}
          >
            <Zap size={14} /> Build my plan
          </button>
        </>
      )}

      {loading && <AnalyzerProgress totalSeconds={15} testId="fitness-loading" />}
      {error && !loading && error.type === "rate_limit" && (
        <LockedFeatureCard
          feature="fitness"
          resetAt={error.reset_at}
          onUpgrade={() => navigate("/premium")}
          onRetry={retry}
          testId="fitness-locked"
        />
      )}
      {error && !loading && error.type !== "rate_limit" && (
        <AnalyzerErrorCard
          error={error}
          onRetry={lastInput ? retry : undefined}
          onPickNew={changePreferences}
          pickNewLabel="Change preferences"
          pickNewIcon={<Sparkles size={14} />}
          feature="fitness-plan"
          testId="fitness-error"
        />
      )}

      {plan && !loading && (
        <>
          <div className="glow-empty-hero" data-testid="fitness-plan-hero" style={{ padding: "22px 22px" }}>
            <div className="glow-empty-badge"><Dumbbell size={12} strokeWidth={2} /> YOUR PLAN</div>
            <div className="h3" style={{ color: "var(--text)", marginTop: 10 }}>{plan.headline}</div>
            {plan.quota && plan.quota.remaining === 0 && !plan.quota.premium && (
              <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 10 }}>
                You&apos;ve used today&apos;s free plan. <button type="button" onClick={() => navigate("/premium")} className="gold-text" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>Upgrade for unlimited</button>.
              </div>
            )}
          </div>

          {plan.weekly_schedule && (
            <div className="outfit-section" data-testid="fitness-week">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Calendar size={12} strokeWidth={1.8} /> WEEKLY SCHEDULE
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {plan.weekly_schedule.map((d, i) => (
                  <div key={i} className="card" style={{ padding: 12, display: "flex", gap: 12, alignItems: "center" }} data-testid={`fitness-day-${i}`}>
                    <div style={{ width: 38, textAlign: "center", color: "var(--gold)", fontFamily: "'Fraunces', serif", fontSize: 14 }}>{d.day}</div>
                    <div style={{ flex: 1 }}>
                      <div className="h3" style={{ color: "var(--text)", fontSize: 13 }}>{d.title}</div>
                      <div className="tiny" style={{ marginTop: 2, color: "var(--text-faint)" }}>
                        {d.focus} · {d.duration_min}min · {d.intensity}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plan.exercises_today?.length > 0 && (
            <div className="outfit-section" data-testid="fitness-today">
              <div className="eyebrow" style={{ marginBottom: 10 }}>EXERCISES — TODAY</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {plan.exercises_today.map((x, i) => {
                  const subtitle = `${x.sets}×${x.reps} · ${x.rest_sec}s rest`;
                  return (
                    <div key={i} className="card" style={{ padding: 12 }} data-testid={`fitness-ex-${i}`}>
                      <ExerciseLine text={x.name} testId={`fitness-ex-${i}-visual`} />
                      <div className="tiny" style={{ marginTop: 6, marginLeft: 70, color: "var(--text-faint)" }}>
                        {subtitle}
                      </div>
                      {x.notes && (
                        <div className="body-sm" style={{ marginTop: 6, marginLeft: 70, color: "var(--text-dim)" }}>
                          {x.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {plan.progress_tips?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10 }}>HOW TO TRACK</div>
              <ul className="bullet-list">{plan.progress_tips.map((t, i) => <li key={i} data-testid={`fitness-tip-${i}`}>{t}</li>)}</ul>
            </div>
          )}

          {plan.watch_outs?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={12} strokeWidth={1.8} /> WATCH OUTS
              </div>
              <ul className="bullet-list">{plan.watch_outs.map((w, i) => <li key={i} style={{ color: "var(--text-dim)" }}>{w}</li>)}</ul>
            </div>
          )}

          <button type="button" className="btn btn-outline" onClick={() => { setPlan(null); }} data-testid="fitness-new-btn" style={{ marginTop: 14, width: "100%", justifyContent: "center" }}>
            <RotateCw size={14} /> Build another plan
          </button>
        </>
      )}
    </div>
  );
}
